import { useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MoneyInput, caretToEnd } from '@/components/ui/money-input';
import { maskBRLInput, numberToMaskedBRL, parseBRLInput } from '@/lib/currencyInput';
import { cn } from '@/lib/utils';

/**
 * Desconto: a UNIDADE vem primeiro, o valor depois — é ela que decide como o
 * número é lido, então escolhê-la antes de digitar é a ordem natural.
 *
 * Os DOIS modos usam a mesma máscara (dígitos pela direita, 2 casas, cursor preso
 * no fim); só mudam o símbolo e o teto (percentual para em 100). Máscara num modo
 * e digitação livre no outro seria a pior combinação: a mesma tecla produziria
 * números diferentes conforme a unidade, e o usuário não teria como saber qual
 * regra está valendo naquele instante.
 */
export function DiscountField({
  value,
  type,
  onValueChange,
  onTypeChange,
  className,
  id,
  disabled,
}: {
  value: number | null | undefined;
  type: 'value' | 'percent';
  onValueChange: (value: number) => void;
  onTypeChange: (type: 'value' | 'percent') => void;
  className?: string;
  id?: string;
  disabled?: boolean;
}) {
  const [percentText, setPercentText] = useState<string | null>(null);

  return (
    <div className={cn('flex items-start gap-1', className)}>
      <Select
        value={type}
        onValueChange={(v) => onTypeChange(v as 'value' | 'percent')}
        disabled={disabled}
      >
        <SelectTrigger className="h-10 w-16 shrink-0 rounded-xl bg-background px-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-popover">
          <SelectItem value="value">R$</SelectItem>
          <SelectItem value="percent">%</SelectItem>
        </SelectContent>
      </Select>
      {type === 'value' ? (
        <MoneyInput
          compact
          id={id}
          value={value}
          onCommit={onValueChange}
          disabled={disabled}
          className="min-w-0 flex-1"
          aria-label="Desconto"
        />
      ) : (
        <div className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
            %
          </span>
          <Input
            id={id}
            inputMode="numeric"
            placeholder="0,00"
            disabled={disabled}
            aria-label="Desconto"
            value={percentText ?? numberToMaskedBRL(value)}
            onChange={(e) => {
              let text = maskBRLInput(e.target.value);
              // O TETO vale para o TEXTO, não só para o valor comitado: clampar
              // apenas o que é gravado deixa a tela mostrando "250,00%" enquanto
              // a conta usa 100% — tela e dado divergindo em silêncio.
              if (parseBRLInput(text) > 100) text = '100,00';
              setPercentText(text);
              onValueChange(parseBRLInput(text));
            }}
            onFocus={caretToEnd}
            onClick={caretToEnd}
            onSelect={caretToEnd}
            onBlur={() => setPercentText(null)}
            className="h-10 w-full rounded-xl bg-background pr-6 text-right text-sm"
          />
        </div>
      )}
    </div>
  );
}
